import { DialogueConfirmation } from '@/components/dialogue/dialogue-confirmation';
import { ecarterEchec } from '@/components/historique/echecs-ecartes';
import { SessionExpireeError, getApi } from '@/lib/api';
import {
  type Brouillon,
  ecouterRelance,
  ecouterVidage,
  enregistrerBrouillon,
  lireBrouillon,
  lireReprise,
  viderBrouillon,
  viderReprise,
} from '@/lib/brouillon';
import { rechercheVersLogin } from '@/lib/garde-session';
import { useToast } from '@/lib/notifications';
import { effacerSession, lireSession } from '@/lib/session';
import { inscrireSuivi } from '@/lib/suivi-traductions';
import { useTitrePage } from '@/lib/titre-page';
import { PageLayout } from '@pms-digital/design-system';
import { LONGUEUR_MAX_TEXTE, demandeTraductionSchema, longueurTexte } from '@rosetta/shared';
import { useNavigate } from '@tanstack/react-router';
import { useEffect, useMemo, useRef, useState } from 'react';
import { LigneTitre } from './ligne-titre';
import { ErreurFichier, MESSAGE_FICHIER_ILLISIBLE, lireFichier } from './lire-fichier';
import { PiedSaisie } from './pied-saisie';
import { proposerTitre } from './titre-propose';
import { ZoneTexte } from './zone-texte';

/** Toast shown when the translation fails, whatever the reason. */
export const MESSAGE_ECHEC = "La traduction n'a pas abouti, réessayez.";

const BROUILLON_VIDE: Brouillon = { titre: '', texte: '', noyaux: [] };

/** A file read: its text and its name. */
interface Fichier {
  contenu: string;
  nom: string;
}

/**
 * The input screen (`/`): title, text zone, noyaux and "traduire". The draft (title,
 * text, noyaux) is kept in `sessionStorage` at every change and read again when the
 * screen opens. "traduire" starts the translation, then opens its waiting screen
 * (`/resultat/<id>`, W1, #7): the input is locked only for the time of the launch, never
 * while the translation runs. As soon as the translation is launched the draft is emptied (#24):
 * every later arrival on this screen is an empty one, with no noyau checked. A launch that fails
 * unlocks the screen with a toast and the draft untouched.
 *
 * The home button of the navbar empties the screen, even a typed text (#26).
 *
 * "Relancer" opens the screen with a text handed over in memory
 * (`lireReprise`, #24): it is shown, but not written in the draft of the tab, so leaving the screen
 * without translating leaves nothing behind.
 *
 * A text longer than 10 000 characters is not translated (#13): a counter shows from 9 000,
 * "traduire" stays off beyond the limit and says why, and a file whose text is longer is refused
 * whole. Nothing is ever cut.
 */
export function SaisieEcran() {
  const navigate = useNavigate();
  const toast = useToast();
  useTitrePage('Traduire un texte');

  // The draft lives in state for the render and in a ref for the async callbacks (a
  // file is read after the user may have typed again).
  const [depart] = useState(() => {
    const reprise = lireReprise();
    return { brouillon: reprise ?? lireBrouillon() ?? BROUILLON_VIDE, repris: reprise !== null };
  });
  const [brouillon, setBrouillon] = useState<Brouillon>(depart.brouillon);
  const courant = useRef(brouillon);
  // A text handed over by "relancer" is never written in the draft of the tab.
  const repris = useRef(depart.repris);
  // Files: the message under the zone, the name of the file being read (S13), the content
  // waiting for the confirmation to replace the text, and the name of the file the text
  // comes from (the source of the proposed title).
  const [erreurFichier, setErreurFichier] = useState<string | null>(null);
  const [lecture, setLecture] = useState<string | null>(null);
  const [contenuARemplacer, setContenuARemplacer] = useState<Fichier | null>(null);
  const [nomFichier, setNomFichier] = useState<string | null>(null);
  const lectureEnVol = useRef(false);
  // Bumped by each emptying (#26): a file being read at that moment must not fill the zone after it.
  const generation = useRef(0);
  const [annonce, setAnnonce] = useState('');
  const [lancement, setLancement] = useState(false);
  // Closes the window between two clicks before the button is disabled: each translation costs.
  const lancementEnVol = useRef(false);

  const enCours = lancement;
  // The title the field proposes: the file name while the text comes from a file, else its first words.
  const titrePropose = proposerTitre(brouillon.texte, nomFichier);
  // The length of the text (#13): computed once per text, not at every render of the screen.
  const longueur = useMemo(() => longueurTexte(brouillon.texte), [brouillon.texte]);

  // The text handed over is taken: nothing waits for the next screen.
  useEffect(() => viderReprise(), []);

  // "Relancer" (a toast, a card) hands a text over while this screen may already be open.
  useEffect(
    () =>
      ecouterRelance(() => {
        const relance = lireReprise();
        if (relance === null) return;
        viderReprise();
        repris.current = true;
        courant.current = relance;
        setBrouillon(relance);
        setNomFichier(null);
        setErreurFichier(null);
      }),
    [],
  );

  // "Accueil" (#26) empties this screen on screen, not only the draft of the tab; the focus stays
  // where it is (the home button the user has just used), as on any arrival on this screen.
  useEffect(
    () =>
      ecouterVidage(() => {
        generation.current += 1;
        repris.current = false;
        courant.current = BROUILLON_VIDE;
        setBrouillon(BROUILLON_VIDE);
        setNomFichier(null);
        setErreurFichier(null);
        setContenuARemplacer(null);
        setLecture(null);
        // A changing text, so that a second emptying is announced again.
        setAnnonce((avant) => (avant === 'Saisie vidée' ? 'Saisie vidée ' : 'Saisie vidée'));
      }),
    [],
  );

  function modifier(patch: Partial<Brouillon>) {
    courant.current = { ...courant.current, ...patch };
    setBrouillon(courant.current);
    if (!repris.current) enregistrerBrouillon(courant.current);
  }

  /** The text of a file now fills the zone; its name is where the proposed title comes from. */
  function remplirDepuis({ contenu, nom }: Fichier) {
    modifier({ texte: contenu });
    setNomFichier(nom);
  }

  async function traiterFichier(fichier: File) {
    // One file at a time: the zone and the button are locked while one is read.
    if (lectureEnVol.current || enCours) return;
    lectureEnVol.current = true;
    const miseAJour = generation.current;
    setLecture(fichier.name);
    setErreurFichier(null);
    let contenu: string;
    try {
      contenu = await lireFichier(fichier);
    } catch (erreur) {
      if (miseAJour !== generation.current) return;
      setErreurFichier(
        erreur instanceof ErreurFichier ? erreur.message : MESSAGE_FICHIER_ILLISIBLE,
      );
      return;
    } finally {
      lectureEnVol.current = false;
      setLecture(null);
    }
    if (miseAJour !== generation.current) return;
    if (courant.current.texte.trim() === '') remplirDepuis({ contenu, nom: fichier.name });
    else setContenuARemplacer({ contenu, nom: fichier.name });
  }

  function remplacer() {
    if (contenuARemplacer !== null) remplirDepuis(contenuARemplacer);
    setContenuARemplacer(null);
  }

  function sessionExpiree() {
    effacerSession();
    void navigate({ to: '/login', search: rechercheVersLogin('session-expiree') });
  }

  function echec() {
    toast.afficher({ variant: 'erreur', message: MESSAGE_ECHEC });
  }

  async function traduire() {
    const { titre, texte, noyaux } = courant.current;
    // The title typed, else the one the field proposes (empty when the text gives none).
    const titreNet = titre.trim() === '' ? titrePropose : titre.trim();
    const demande = demandeTraductionSchema.safeParse({
      ...(titreNet === '' ? {} : { titre: titreNet }),
      texte,
      noyaux,
    });
    if (!demande.success || lancementEnVol.current) return;

    const session = lireSession();
    if (session === null) {
      void navigate({ to: '/login', search: rechercheVersLogin('session-expiree') });
      return;
    }

    lancementEnVol.current = true;
    setLancement(true);
    try {
      const id = await getApi().lancerTraduction(session, demande.data);
      // Followed from now on: its waiting screen takes it over (marked as awaited at once, so
      // the follow-up of the whole app never reads it in between), and the follow-up gets it
      // back when the user leaves that screen while it runs.
      inscrireSuivi(
        { id, titre: demande.data.titre ?? '', utilisateurId: session.utilisateur.id },
        { attendue: true },
      );
      // The translation is launched: its text, title and noyaux are not kept (#24). A relaunch that
      // is sent also makes the card of the failed translation it came from go away.
      if (courant.current.relanceDe !== undefined) {
        ecarterEchec(session.utilisateur.id, courant.current.relanceDe);
      }
      viderBrouillon();
      viderReprise();
      await navigate({ to: '/resultat/$id', params: { id } });
    } catch (erreur) {
      if (erreur instanceof SessionExpireeError) sessionExpiree();
      else echec();
    } finally {
      lancementEnVol.current = false;
      setLancement(false);
    }
  }

  return (
    <>
      {/* Page of the package, « narrow » (720 px) as every input screen. Under 1 280 px the footer
          follows the column; from 1 280 px it is fixed at the bottom of the screen (80 px), above
          the 64 px kept under the column and the 60 px margin of the page. */}
      <PageLayout width="narrow">
        <div aria-busy={enCours} className="flex flex-col xl:pb-16">
          <h1 className="text-h2 leading-[1.2] tracking-normal">
            <span className="font-bold">Traduire</span> <span className="font-light">un texte</span>
          </h1>
          <p className="mt-2 font-light text-body text-gray-700 leading-[1.55]">
            Saisissez ou chargez votre texte, puis choisissez les noyaux vers lesquels le traduire.
          </p>
          <LigneTitre
            titre={brouillon.titre}
            onTitreChange={(titre) => modifier({ titre })}
            titrePropose={titrePropose}
            verrouille={enCours}
            lecture={lecture !== null}
            onFichierChoisi={(fichier) => void traiterFichier(fichier)}
          />
          <ZoneTexte
            valeur={brouillon.texte}
            onChange={(texte) => {
              setErreurFichier(null);
              if (texte.trim() === '') setNomFichier(null);
              modifier({ texte });
            }}
            verrouille={enCours}
            erreurFichier={erreurFichier}
            lecture={lecture}
            onFichierDepose={(fichier) => void traiterFichier(fichier)}
            longueur={longueur}
          />
          <div aria-live="polite" className="sr-only">
            {annonce}
          </div>
        </div>
      </PageLayout>
      <PiedSaisie
        noyaux={brouillon.noyaux}
        onNoyauxChange={(noyaux) => modifier({ noyaux })}
        texteVide={brouillon.texte.trim() === ''}
        texteTropLong={longueur > LONGUEUR_MAX_TEXTE}
        enCours={enCours}
        onTraduire={() => void traduire()}
      />
      <DialogueConfirmation
        open={contenuARemplacer !== null}
        title="Remplacer le texte actuel par le contenu du fichier ?"
        description="Le texte déjà saisi sera perdu."
        icon="upload_file"
        cancelLabel="annuler"
        confirmLabel="remplacer"
        onCancel={() => setContenuARemplacer(null)}
        onConfirm={remplacer}
      />
    </>
  );
}
